import { tracking } from '../../../../../utils/tracking';
import { trackDirect } from '../../../../../store/analytics/api';
import { REFERRAL_SOURCE } from '../../../../../constants/referral';

export const STUDIO_TRACKING_SOURCE = {
  HOT_TOPIC: 'hot-topic-studio',
  CUSTOM_NEWSFEED: 'custom-newsfeed'
} as const;

export type StudioTrackingSource = (typeof STUDIO_TRACKING_SOURCE)[keyof typeof STUDIO_TRACKING_SOURCE];

export type StudioTrackAction = 'viewed' | 'downloaded' | 'shared' | 'generate' | 'saveImage';

type StudioTrackEvent = {
  mixpanel: string;
  direct: string | null;
};

const HOT_TOPIC_EVENTS: Record<StudioTrackAction, StudioTrackEvent> = {
  viewed: { mixpanel: 'Hot Topic Studio Viewed', direct: 'viewHotTopicStudio' },
  downloaded: { mixpanel: 'Hot Topic Studio Downloaded', direct: 'downloadHotTopicStudio' },
  shared: { mixpanel: 'Hot Topic Studio Shared', direct: 'shareHotTopicStudio' },
  generate: { mixpanel: 'Hot Topic Studio Generate', direct: 'generateHotTopicStudio' },
  saveImage: { mixpanel: 'Save Hot Topic Studio Image', direct: null }
};

const CUSTOM_NEWSFEED_EVENTS: Record<StudioTrackAction, StudioTrackEvent> = {
  viewed: { mixpanel: 'Custom Newsfeed Studio Viewed', direct: 'viewCustomNewsfeed' },
  downloaded: { mixpanel: 'Custom Newsfeed Studio Downloaded', direct: 'downloadCustomNewsfeed' },
  shared: { mixpanel: 'Custom Newsfeed Studio Shared', direct: 'shareCustomNewsfeed' },
  generate: { mixpanel: 'Custom Newsfeed Studio Generate', direct: 'generateCustomNewsfeed' },
  saveImage: { mixpanel: 'Save Custom Newsfeed Studio Image', direct: null }
};

const resolveSource = (trackingSource?: StudioTrackingSource): StudioTrackingSource =>
  trackingSource === STUDIO_TRACKING_SOURCE.CUSTOM_NEWSFEED
    ? STUDIO_TRACKING_SOURCE.CUSTOM_NEWSFEED
    : STUDIO_TRACKING_SOURCE.HOT_TOPIC;

export const getStudioReferralSource = (trackingSource?: StudioTrackingSource): string =>
  resolveSource(trackingSource) === STUDIO_TRACKING_SOURCE.CUSTOM_NEWSFEED
    ? REFERRAL_SOURCE.CUSTOM_NEWSFEED
    : REFERRAL_SOURCE.HOT_TOPIC_STUDIO;

export const getStudioTrackEvents = (
  trackingSource?: StudioTrackingSource
): Record<StudioTrackAction, StudioTrackEvent> =>
  resolveSource(trackingSource) === STUDIO_TRACKING_SOURCE.CUSTOM_NEWSFEED
    ? CUSTOM_NEWSFEED_EVENTS
    : HOT_TOPIC_EVENTS;

export const trackStudioAction = (
  trackingSource: StudioTrackingSource | undefined,
  action: StudioTrackAction,
  properties: { id?: string; format?: string; pages?: number } = {},
  trackingFn: typeof tracking = tracking,
  trackDirectFn: typeof trackDirect = trackDirect
) => {
  const source = resolveSource(trackingSource);
  const event = getStudioTrackEvents(source)[action];

  trackingFn(event.mixpanel, { ...properties, source });

  if (event.direct) {
    trackDirectFn(event.direct, properties.id);
  }

  // Newsfeed studio shares should count as newsfeed, not lazy pack.
  if (source === STUDIO_TRACKING_SOURCE.CUSTOM_NEWSFEED && action === 'shared') {
    trackDirectFn('shareSelectedNewsfeed', properties.id);
  }
};
